/**
 * Generated-file templates for the Node SDK installer (ADR-069 §1).
 *
 * Three flavors so the inserted file matches the host package's module
 * system: CJS for `require`-based packages, ESM for `import`-based, TS for
 * TypeScript entries. Env vars are inlined as `process.env.X ||=` defaults
 * rather than loaded from `.env.neat` at runtime — `__dirname` /
 * `import.meta.url` resolve unpredictably once a bundler rewrites the
 * generated file (Brief-smoke evidence, issue #369), so we let the OTel SDK
 * read its config from the same `process.env` either way. Platform env
 * (Vercel / Railway / Fly / etc.) stays authoritative at deploy time;
 * `||=` keeps the local default for laptop dev only.
 */

// Header comment that ships at the top of every generated otel-init. Stable
// across flavors so a grep for the header line finds every generated file in
// a target codebase. Used by the idempotency check too (ADR-069 §6).
export const OTEL_INIT_HEADER = '// Generated by `neat init --apply` (ADR-069). OpenTelemetry auto-instrumentation hook.'

// Version stamp on the generated otel-init (file-awareness.md §4). A NEAT-owned
// file carries OTEL_INIT_HEADER; the stamp records which template revision wrote
// it. On re-run, an existing NEAT-owned file whose stamp is older than the
// current one is regenerated (a hand-written init — no header — is never
// touched). Bump the revision whenever the generated body changes shape.
export const OTEL_INIT_STAMP =
  '// neat-template-version: 6 — daemon.json endpoint resolution (ADR-096) + layered file-first capture (ADR-090).'

// OTLP exporter auth (ADR-073 §3/§4, #410). When the operator runs the
// instrumented app with `NEAT_OTEL_TOKEN` set — the same secret the daemon's
// OTLP receiver reads (`NEAT_OTEL_TOKEN ?? NEAT_AUTH_TOKEN`) — the exporter
// sends `Authorization: Bearer <token>` so a secured daemon accepts the spans.
// Unset (laptop dev against a loopback daemon with no token) sends no header,
// matching the daemon's unauthenticated loopback path. The token is the single
// source: it lives in the environment, never inlined into the generated file.
// `||=` keeps any platform-set header (Vercel / Railway / Fly) authoritative.
export const OTEL_OTLP_HEADERS_JS =
  "if (process.env.NEAT_OTEL_TOKEN) process.env.OTEL_EXPORTER_OTLP_HEADERS ||= 'Authorization=Bearer ' + process.env.NEAT_OTEL_TOKEN"

// Export protocol pin (#468). The OTel JS SDK defaults to http/protobuf; NEAT's
// receiver accepts both, but http/json is the wire format the smoke gates
// validate hardest, so the generated init pins it. Same `||=` discipline as the
// other inlined env: an operator who explicitly sets
// OTEL_EXPORTER_OTLP_PROTOCOL (e.g. to route through a collector that insists
// on protobuf) stays authoritative.
export const OTEL_OTLP_PROTOCOL_JS =
  "process.env.OTEL_EXPORTER_OTLP_PROTOCOL ||= 'http/json'"

// OTLP endpoint resolution (ADR-096 / project-daemon contract). Under
// one-daemon-per-project the daemon allocates its OTLP port once (canonical
// 4318, stepping to the next free port when a sibling daemon already holds it)
// and records it in `<project>/neat-out/daemon.json`. The instrumented app must
// read THAT port back, or a second project's app — baked to a fixed 4318 —
// sends its spans to the first project's daemon, which has no slot for them and
// drops them: the second project's OBSERVED layer goes dark silently. That is
// exactly the failure this code exists to kill, so the endpoint is resolved at
// app boot from the daemon's own record rather than hardcoded.
//
// Precedence (highest first):
//   1. An explicit OTEL_EXPORTER_OTLP_TRACES_ENDPOINT / _ENDPOINT env var —
//      the prod/hosted path where a platform or collector owns the target.
//   2. `<project>/neat-out/daemon.json` → `ports.otlp` → the bare
//      `http://localhost:<otlp>/v1/traces` route the per-project daemon serves.
//   3. The canonical `http://localhost:4318/v1/traces` default — the laptop
//      happy path where the daemon took its first-choice port.
//
// This is INJECTED into the user's app, so it is bulletproof: the whole thing
// is wrapped in try/catch, a missing or malformed daemon.json falls through to
// the canonical default, and it NEVER throws — a resolution failure must not
// crash the host app's startup. The daemon.json path is resolved by walking up
// from the app's runtime cwd looking for `neat-out/daemon.json`, which lands on
// the project root whether the app boots from the project root (single package)
// or a package subdirectory (monorepo).
//
// The endpoint matches the bare `/v1/traces` route the per-project daemon
// serves (daemon.ts) — NOT the legacy `/projects/<name>/v1/traces` form, since
// a daemon that hosts exactly one project needs no project name to disambiguate.

// CJS flavor — `require('node:fs')` is available in the require-based template.
export const OTEL_ENDPOINT_RESOLVER_CJS = `;(function () {
  try {
    if (process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT || process.env.OTEL_EXPORTER_OTLP_ENDPOINT) return
    const __neatFs = require('node:fs')
    const __neatPath = require('node:path')
    let __neatDir = process.cwd()
    for (let __i = 0; __i < 8; __i++) {
      try {
        const __rec = JSON.parse(__neatFs.readFileSync(__neatPath.join(__neatDir, 'neat-out', 'daemon.json'), 'utf8'))
        if (__rec && __rec.ports && typeof __rec.ports.otlp === 'number') {
          process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT = 'http://localhost:' + __rec.ports.otlp + '/v1/traces'
          break
        }
      } catch (_e) {}
      const __parent = __neatPath.dirname(__neatDir)
      if (__parent === __neatDir) break
      __neatDir = __parent
    }
  } catch (_e) {}
  process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT ||= 'http://localhost:4318/v1/traces'
})()`

// ESM/TS flavor — `require` is not defined in a pure-ESM module, so the
// built-in fs/path come in via the hoisted `import` block
// OTEL_ESM_NODE_IMPORTS adds to the top of the generated ESM/TS file. The
// resolver references those bindings (`__neatReadFileSync` / `__neatJoin` /
// `__neatDirname`) directly. ES import bindings are evaluated before this IIFE
// runs, so they're always present.
export const OTEL_ESM_NODE_IMPORTS =
  "import { readFileSync as __neatReadFileSync } from 'node:fs'\n" +
  "import { join as __neatJoin, dirname as __neatDirname } from 'node:path'"

export const OTEL_ENDPOINT_RESOLVER_ESM = `;(function () {
  try {
    if (process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT || process.env.OTEL_EXPORTER_OTLP_ENDPOINT) return
    let __neatDir = process.cwd()
    for (let __i = 0; __i < 8; __i++) {
      try {
        const __rec = JSON.parse(__neatReadFileSync(__neatJoin(__neatDir, 'neat-out', 'daemon.json'), 'utf8'))
        if (__rec && __rec.ports && typeof __rec.ports.otlp === 'number') {
          process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT = 'http://localhost:' + __rec.ports.otlp + '/v1/traces'
          break
        }
      } catch (_e) {}
      const __parent = __neatDirname(__neatDir)
      if (__parent === __neatDir) break
      __neatDir = __parent
    }
  } catch (_e) {}
  process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT ||= 'http://localhost:4318/v1/traces'
})()`

// Inlined layered call-site capture (file-awareness.md §4–6, ADR-090). The
// whole mechanism is self-contained — NEAT can't import its own package into a
// user's generated file, so the runtime logic is inlined verbatim. The exact
// bytes that ship are exercised by the capture spike
// (test/callsite-processor.test.ts) so a regression here fails CI before it
// ships. The `ts` flag adds the few type annotations a strict tsconfig needs;
// the runtime logic is identical across flavors.
//
// Three layers land the user's `file:line:function` on every CLIENT / PRODUCER
// / SERVER span:
//   1. Synchronous stack walk at span start — covers the sync-wrapper majority.
//   2. Handler-entry attribution — stamps the framework SERVER span and pushes
//      the handler frame into the active context so downstream CLIENT/PRODUCER
//      spans inherit it (the floor).
//   3. Off-stack facades — undici / built-in `fetch` (diagnostics_channel) and
//      Prisma (backdated dispatch) push the call-site frame into context for
//      the inner call; the processor reads it as a fallback.
function neatCaptureSource(ts: boolean): string {
  const spanT = ts ? ': any' : ''
  const strOpt = ts ? ': string | undefined' : ''
  const anyT = ts ? ': any' : ''
  const fnT = ts ? ': any' : ''
  const arrAny = ts ? ': any[]' : ''
  return `// Context key shared by the facade/handler wraps (writers) and the
// processor fallback (reader). Symbol.for keeps it stable even if the wraps
// and the processor end up in different module instances.
const NEAT_USER_FRAME = Symbol.for('neat.user-frame')

function __neatPickUserFrame(stack${strOpt}) {
  const lines = String(stack || '').split('\\n')
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i].trim()
    if (raw.indexOf('at ') !== 0) continue
    if (raw.indexOf('node_modules') !== -1) continue
    if (raw.indexOf('@opentelemetry') !== -1) continue
    if (raw.indexOf('node:') !== -1) continue
    if (raw.indexOf('NeatCallSiteSpanProcessor') !== -1) continue
    // Skip NEAT's own inlined wraps/helpers (all carry the __neat prefix) so a
    // facade frame never masquerades as the user's call site.
    if (raw.indexOf('__neat') !== -1) continue
    const bodyText = raw.slice(3)
    const loc = bodyText.match(/:(\\d+):(\\d+)\\)?$/)
    if (!loc) continue
    const paren = bodyText.lastIndexOf('(')
    let filepath${strOpt}
    let fn${strOpt}
    if (paren !== -1) {
      fn = bodyText.slice(0, paren).trim()
      if (fn.indexOf('async ') === 0) fn = fn.slice(6)
      if (fn.indexOf('new ') === 0) fn = fn.slice(4)
      filepath = bodyText.slice(paren + 1, bodyText.length - loc[0].length)
    } else {
      filepath = bodyText.slice(0, bodyText.length - loc[0].length)
    }
    if (filepath.indexOf('file://') === 0) filepath = filepath.slice(7)
    if (!filepath) continue
    return { filepath: filepath, lineno: Number(loc[1]), function: fn || undefined }
  }
  return null
}

// Layer 2/3 fallback source: the frame a handler-entry or facade wrap pushed
// into context. Reads the span's own parent context first, then the active
// context — the capture spike confirmed both return the value for undici.
function __neatFrameFromContext(parentContext${anyT}) {
  try {
    const fromParent =
      parentContext && typeof parentContext.getValue === 'function'
        ? parentContext.getValue(NEAT_USER_FRAME)
        : undefined
    return fromParent || context.active().getValue(NEAT_USER_FRAME) || null
  } catch (_e) {
    return null
  }
}

function __neatSetCodeAttrs(span${spanT}, frame${anyT}) {
  span.setAttribute('code.filepath', frame.filepath)
  if (typeof frame.lineno === 'number') span.setAttribute('code.lineno', frame.lineno)
  if (frame.function) span.setAttribute('code.function', frame.function)
}

class NeatCallSiteSpanProcessor {
  onStart(span${spanT}, parentContext${anyT}) {
    if (!span || (span.kind !== 2 && span.kind !== 3)) return
    // Layer 1 — synchronous stack walk (sync-wrapper instrumentations).
    let frame = __neatPickUserFrame(new Error().stack)
    // Layer 2/3 — the handler-entry or off-stack-facade frame from context.
    if (!frame) frame = __neatFrameFromContext(parentContext)
    if (!frame) return
    __neatSetCodeAttrs(span, frame)
  }
  onEnd() {}
  forceFlush() { return Promise.resolve() }
  shutdown() { return Promise.resolve() }
}

// Capture the caller's synchronous frame at the wrap point and run \`fn\` with
// that frame pushed into the active context (file-awareness.md §4 layer 3). An
// off-stack instrumentation that creates its span inside \`fn\` inherits the
// frame; the processor reads it via __neatFrameFromContext.
function __neatRunWithUserFrame(fn${fnT}) {
  const frame = __neatPickUserFrame(new Error().stack)
  if (!frame) return fn()
  return context.with(context.active().setValue(NEAT_USER_FRAME, frame), fn)
}

// Handler-entry attribution (file-awareness.md §4 layer 2). Stamp the framework
// SERVER span with the handler frame captured at route registration, and push
// the same frame into context so downstream CLIENT/PRODUCER spans inherit the
// handler-file floor when their own stack carries none.
function __neatStampHandler(frame${anyT}, run${fnT}) {
  try {
    const active = trace.getActiveSpan()
    if (active && active.kind === 1 && typeof active.setAttribute === 'function') {
      __neatSetCodeAttrs(active, frame)
    }
  } catch (_e) {}
  try {
    return context.with(context.active().setValue(NEAT_USER_FRAME, frame), run)
  } catch (_e) {
    return run()
  }
}

// require-in-the-middle is a transitive dependency of the OTel instrumentation
// packages NEAT installs, so it resolves in any instrumented CJS service.
// Guarded: when it's absent (or the host runs as pure ESM, where require isn't
// defined) the off-stack/handler wraps degrade to the stack walk + context
// floor, never to a crash.
function __neatHook(modules${arrAny}, onload${fnT}) {
  try {
    const RITM = require('require-in-the-middle')
    const Hook = RITM && RITM.Hook ? RITM.Hook : RITM
    new Hook(modules, { internals: false }, onload)
    return true
  } catch (_e) {
    return false
  }
}

// Off-stack facade: Node's built-in fetch / undici. The instrumentation creates
// the CLIENT span inside a diagnostics_channel handler detached from the
// caller's stack, so wrap the global so the user frame is in context when the
// span is created. The capture spike (2026-05-28) validated this on real
// undici. .name is restored to 'fetch' for ecosystem compatibility; the inner
// __neat name is what the frame skip keys on.
function __neatWrapFetch() {
  try {
    const g${anyT} = globalThis
    if (typeof g.fetch === 'function' && !g.fetch.__neatWrapped) {
      const realFetch = g.fetch
      // The wrapper keeps its __neat-prefixed name so the frame skip in
      // __neatPickUserFrame never mistakes the wrapper's own frame for the
      // user's call site (renaming it to 'fetch' would defeat the skip).
      const __neatFetch${anyT} = function (input${anyT}, init${anyT}) {
        return __neatRunWithUserFrame(function () { return realFetch(input, init) })
      }
      __neatFetch.__neatWrapped = true
      g.fetch = __neatFetch
    }
  } catch (_e) {}
}

// Off-stack facade: @prisma/client. Prisma's query engine backdates its spans
// from Rust, off the caller's stack. Wrap the model methods on the client
// prototype so the call-site frame (still synchronous at \`prisma.user.find\`)
// is pushed into context for the engine dispatch.
function __neatWrapPrisma() {
  __neatHook(['@prisma/client'], function (exports${anyT}) {
    try {
      const Client = exports && exports.PrismaClient
      if (typeof Client === 'function' && !Client.__neatWrapped) {
        const ops = ['findUnique','findUniqueOrThrow','findFirst','findFirstOrThrow','findMany','create','createMany','update','updateMany','upsert','delete','deleteMany','count','aggregate','groupBy']
        const __neatPrismaWrapModel = function (model${anyT}) {
          if (!model || model.__neatWrapped) return model
          for (let i = 0; i < ops.length; i++) {
            const op = ops[i]
            const orig = model[op]
            if (typeof orig !== 'function') continue
            model[op] = function __neatPrismaOp(${ts ? '...args: any[]' : '...args'}) {
              const self = this
              return __neatRunWithUserFrame(function () { return orig.apply(self, args) })
            }
          }
          model.__neatWrapped = true
          return model
        }
        const proto = Client.prototype
        const handler = function (model${anyT}) { return __neatPrismaWrapModel(model) }
        // Model accessors are lazily created getters on the instance; wrap the
        // \`$extends\`-free common path by trapping property access via a proxy
        // on each constructed client.
        exports.PrismaClient = new Proxy(Client, {
          construct(Target${anyT}, argList${anyT}, NewTarget${anyT}) {
            const instance = Reflect.construct(Target, argList, NewTarget)
            return new Proxy(instance, {
              get(target${anyT}, prop${anyT}, receiver${anyT}) {
                const value = Reflect.get(target, prop, receiver)
                if (value && typeof value === 'object' && typeof prop === 'string' && prop[0] !== '$' && prop[0] !== '_') {
                  return handler(value)
                }
                return value
              },
            })
          },
        })
        exports.PrismaClient.__neatWrapped = true
        void proto
      }
    } catch (_e) {}
    return exports
  })
}

// Handler-entry facades. express / connect share the Layer model (each route
// handler is a function registered on a Router); the wrap captures the
// registration frame and stamps + propagates it when the handler runs. The
// registry is the extensibility seam — koa, fastify (via @fastify/otel),
// nestjs, restify, and hapi add an entry as their patch surface is wired.
function __neatWrapConnectStyle(mod${anyT}) {
  try {
    // express() returns an app whose route verbs live on Router.prototype and
    // the application proto; connect apps expose \`use\`. Wrap the registration
    // verbs so the user handler is wound with its registration frame.
    const verbs = ['use','get','post','put','delete','patch','all','options','head']
    const wrapTarget = function (target${anyT}) {
      if (!target || target.__neatVerbsWrapped) return
      for (let i = 0; i < verbs.length; i++) {
        const verb = verbs[i]
        const orig = target[verb]
        if (typeof orig !== 'function') continue
        target[verb] = function __neatVerb(${ts ? '...args: any[]' : '...args'}) {
          const frame = __neatPickUserFrame(new Error().stack)
          if (frame) {
            for (let a = 0; a < args.length; a++) {
              const h = args[a]
              if (typeof h === 'function' && !h.__neatHandlerWrapped && h.length <= 4) {
                const inner = h
                const __neatHandler${anyT} = function (${ts ? '...hargs: any[]' : '...hargs'}) {
                  const self = this
                  return __neatStampHandler(frame, function () { return inner.apply(self, hargs) })
                }
                __neatHandler.__neatHandlerWrapped = true
                args[a] = __neatHandler
              }
            }
          }
          return orig.apply(this, args)
        }
      }
      target.__neatVerbsWrapped = true
    }
    if (mod && mod.Router && mod.Router.prototype) wrapTarget(mod.Router.prototype)
    if (mod && mod.application) wrapTarget(mod.application)
    if (mod && mod.prototype) wrapTarget(mod.prototype)
  } catch (_e) {}
}

function __neatInstallHandlerEntry() {
  __neatHook(['express'], function (exports${anyT}) { __neatWrapConnectStyle(exports); return exports })
  __neatHook(['connect'], function (exports${anyT}) { __neatWrapConnectStyle(exports); return exports })
}

// Install every off-stack and handler-entry wrap. Called once after sdk.start()
// — before user code requires the framework / Prisma modules, so the hooks land
// on first require. Each wrap is independently guarded.
function __neatInstallFacades() {
  __neatWrapFetch()
  __neatWrapPrisma()
  __neatInstallHandlerEntry()
}`
}

// The runtime capture source, exported so the capture spike can materialise and
// exercise the exact bytes that ship in the generated file.
export const CALLSITE_PROCESSOR_JS = neatCaptureSource(false)
export const CALLSITE_PROCESSOR_TS = neatCaptureSource(true)

// Post-`sdk.start()` wiring. NodeSDK keeps its env-configured OTLP exporter
// (passing `spanProcessors` to the constructor would replace it); we add the
// call-site processor to the started provider via the public `getDelegate()` +
// `addSpanProcessor()` surface, then assert it actually attached (file-
// awareness.md §4 / ADR-090). A wiring regression that would silently drop
// file-first capture throws loudly at boot instead of shipping service-level-
// only spans. The facade/handler wraps install last, so a span export path is
// live before any user module loads.
function neatWireCaptureSource(ts: boolean): string {
  const providerT = ts ? ': any' : ''
  return `try {
  const provider${providerT} = trace.getTracerProvider()
  const delegate = provider && typeof provider.getDelegate === 'function' ? provider.getDelegate() : provider
  if (!delegate || typeof delegate.addSpanProcessor !== 'function') {
    throw new Error('[neat] could not resolve a TracerProvider to attach the call-site processor; file-first OBSERVED capture would be silent (file-awareness.md §4)')
  }
  const __neatProcessor = new NeatCallSiteSpanProcessor()
  delegate.addSpanProcessor(__neatProcessor)
  // Post-init assertion: confirm attachment on providers that expose their
  // processor list. An un-introspectable provider is trusted (we just called
  // its addSpanProcessor); a list that exists and lacks our processor throws.
  const registered = delegate._registeredSpanProcessors
  if (Array.isArray(registered) && registered.indexOf(__neatProcessor) === -1) {
    throw new Error('[neat] call-site processor did not attach to the active TracerProvider (file-awareness.md §4)')
  }
} catch (err) {
  throw err
}
try {
  __neatInstallFacades()
} catch (_e) {
  // Facade install is best-effort: the stack-walk + handler-entry floor still
  // attribute the sync-wrapper majority even if an off-stack wrap fails.
}`
}

// __SERVICE_NAME__ → the ServiceNode id (the package's `pkg.name`, or the
// directory basename for nameless packages); __PROJECT__ → the registered
// project basename; __INSTRUMENTATION_BLOCK__ → a (possibly empty) sequence
// of `instrumentations.push(...)` lines for non-bundled instrumentations
// (Prisma for v0.4.5; more libraries via the v0.5.0 registry). All three are
// substituted at apply time via renderNodeOtelInit().
//
// v0.4.5 — explicit `NodeSDK` construction replaces the
// `auto-instrumentations-node/register` shorthand so non-bundled libraries
// (Prisma's Rust query engine bypasses node-pg; Drizzle, BetterAuth, etc.
// have their own packages) can compose into the same `instrumentations`
// array without templating a different file shape per library.
export const OTEL_INIT_CJS = `${OTEL_INIT_HEADER}
${OTEL_INIT_STAMP}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_CJS}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

const { NodeSDK } = require('@opentelemetry/sdk-node')
const { getNodeAutoInstrumentations } = require('@opentelemetry/auto-instrumentations-node')
const { trace, context } = require('@opentelemetry/api')

${CALLSITE_PROCESSOR_JS}

const instrumentations = [getNodeAutoInstrumentations()]
__INSTRUMENTATION_BLOCK__
const sdk = new NodeSDK({ instrumentations })
sdk.start()
${neatWireCaptureSource(false)}
`

export const OTEL_INIT_ESM = `${OTEL_INIT_HEADER}
${OTEL_INIT_STAMP}
${OTEL_ESM_NODE_IMPORTS}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_ESM}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

import { NodeSDK } from '@opentelemetry/sdk-node'
import { getNodeAutoInstrumentations } from '@opentelemetry/auto-instrumentations-node'
import { trace, context } from '@opentelemetry/api'

${CALLSITE_PROCESSOR_JS}

const instrumentations = [getNodeAutoInstrumentations()]
__INSTRUMENTATION_BLOCK__
const sdk = new NodeSDK({ instrumentations })
sdk.start()
${neatWireCaptureSource(false)}
`

export const OTEL_INIT_TS = `${OTEL_INIT_HEADER}
${OTEL_INIT_STAMP}
// @ts-nocheck — generated runtime shim. The layered capture mechanism uses
// dynamic patterns (facade wraps, Proxy traps, a createRequire bridge) that a
// strict user tsconfig would reject; suppressing type-checking here keeps the
// generated file from breaking the host project's \`tsc\` gate (#427) without
// constraining the runtime logic. The file is regenerated, never hand-edited.
${OTEL_ESM_NODE_IMPORTS}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_ESM}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

import { NodeSDK } from '@opentelemetry/sdk-node'
import { getNodeAutoInstrumentations } from '@opentelemetry/auto-instrumentations-node'
import { trace, context } from '@opentelemetry/api'

${CALLSITE_PROCESSOR_TS}

const instrumentations = [getNodeAutoInstrumentations()]
__INSTRUMENTATION_BLOCK__
const sdk = new NodeSDK({ instrumentations })
sdk.start()
${neatWireCaptureSource(true)}
`

// Substitute __SERVICE_NAME__, __PROJECT__, and __INSTRUMENTATION_BLOCK__
// at apply time. The third arg is a list of registration snippets — one per
// non-bundled instrumentation the installer detected (e.g. Prisma). An empty
// list collapses the placeholder to nothing so the generated file stays
// readable on the common "auto-instrumentations covers everything" path.
export function renderNodeOtelInit(
  template: string,
  serviceName: string,
  projectName: string,
  registrations: readonly string[] = [],
): string {
  const block = registrations.length === 0 ? '' : `\n${registrations.join('\n')}\n`
  return template
    .replace(/__SERVICE_NAME__/g, serviceName)
    .replace(/__PROJECT__/g, projectName)
    .replace(/__INSTRUMENTATION_BLOCK__\n?/g, block)
}

// .env.neat shape (ADR-069 §4, amended v0.4.1 — refs #339, v0.4.4 — refs
// #367 + #369, v0.4.8 — refs #410, ADR-096). Two keys: OTEL_SERVICE_NAME (the
// ServiceNode id — the package's own name, scope-preserved, so spans land on
// per-service nodes inside the project's graph) and
// OTEL_EXPORTER_OTLP_TRACES_ENDPOINT. Under one-daemon-per-project (ADR-096)
// the daemon serves a bare `/v1/traces` route and allocates its OTLP port once,
// recording it in `<project>/neat-out/daemon.json`; the generated `otel-init`
// reads that record at boot and ignores this file, so the value here is purely
// the canonical-default advisory the operator sees when grepping for the
// service.name → endpoint mapping. Both env vars are advisory only — the
// generated `otel-init` inlines/derives them so bundlers can't lose them. A
// commented `NEAT_OTEL_TOKEN` hint documents the single source of the OTLP
// bearer (#410): set it to the secret the daemon's receiver expects and the
// generated init sends `Authorization: Bearer <token>`.
export function renderEnvNeat(serviceName: string, _projectName: string): string {
  return [
    '# Generated by `neat init --apply` (ADR-069).',
    `OTEL_SERVICE_NAME=${serviceName}`,
    '# Advisory only — the generated otel-init resolves the live endpoint from',
    '# <project>/neat-out/daemon.json (ports.otlp) at boot (ADR-096). This is the',
    '# canonical default the daemon takes when its first-choice OTLP port is free.',
    'OTEL_EXPORTER_OTLP_TRACES_ENDPOINT=http://localhost:4318/v1/traces',
    'OTEL_EXPORTER_OTLP_PROTOCOL=http/json',
    '# Set NEAT_OTEL_TOKEN to the daemon\'s OTLP secret to authenticate exported spans (#410).',
    '# NEAT_OTEL_TOKEN=',
    '',
  ].join('\n')
}

// ── Next.js framework-aware templates (ADR-073 §1, ADR-069 §3, ADR-126). ─
//
// Next.js owns its own boot — `pkg.main` is unused, and the auto-instrumentation
// register hook can't run before user code. Next 13+ ships an `instrumentation`
// file at the project root with an async `register()` export the framework
// invokes during server start, once per runtime it boots. Browser bundles
// still never load this file.
//
// Three-file shape: `instrumentation.{ts,js}` is the runtime gate, dispatching
// to `instrumentation.node.{ts,js}` (the Node SDK init) on the nodejs runtime
// and `instrumentation.edge.{ts,js}` (`@vercel/otel`, ADR-126) on the edge
// runtime — the standard Node SDK can't execute inside Next's Edge runtime at
// all, so the edge file leans on a runtime-aware package instead. All three
// files sit at the project root (or under `src/` for `--src-dir` layouts).

export const NEXT_INSTRUMENTATION_HEADER =
  '// Generated by `neat init --apply` (ADR-073). Next.js instrumentation hook.'

export const NEXT_INSTRUMENTATION_TS = `${NEXT_INSTRUMENTATION_HEADER}
export async function register() {
  if (process.env.NEXT_RUNTIME === 'nodejs') {
    await import('./instrumentation.node')
  }
  if (process.env.NEXT_RUNTIME === 'edge') {
    await import('./instrumentation.edge')
  }
}
`

export const NEXT_INSTRUMENTATION_JS = `${NEXT_INSTRUMENTATION_HEADER}
export async function register() {
  if (process.env.NEXT_RUNTIME === 'nodejs') {
    await import('./instrumentation.node')
  }
  if (process.env.NEXT_RUNTIME === 'edge') {
    await import('./instrumentation.edge')
  }
}
`

// Env vars are inlined as process.env defaults so they survive Turbopack /
// Webpack bundling — `import.meta.url` resolves to a path under .next/dev/
// server/chunks/ once Next compiles this file, which would miss .env.neat.
// `process.env.X ||=` keeps platform env (Vercel / Railway / Fly / etc.)
// authoritative at deploy time while the local default carries the routing
// key the daemon needs to map spans back to this project. The endpoint uses
// the project-scoped URL form so the daemon never has to guess which project
// owns the span (issue #367); the OTel SDK reads the env vars from
// process.env directly, so no file-system lookup is required at runtime.
export const NEXT_INSTRUMENTATION_NODE_TS = `${NEXT_INSTRUMENTATION_HEADER}
${OTEL_ESM_NODE_IMPORTS}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_ESM}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

import { NodeSDK } from '@opentelemetry/sdk-node'
import { getNodeAutoInstrumentations } from '@opentelemetry/auto-instrumentations-node'

const instrumentations = [getNodeAutoInstrumentations()]
__INSTRUMENTATION_BLOCK__
new NodeSDK({ instrumentations }).start()
`

export const NEXT_INSTRUMENTATION_NODE_JS = `${NEXT_INSTRUMENTATION_HEADER}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_CJS}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

const { NodeSDK } = require('@opentelemetry/sdk-node')
const { getNodeAutoInstrumentations } = require('@opentelemetry/auto-instrumentations-node')

const instrumentations = [getNodeAutoInstrumentations()]
__INSTRUMENTATION_BLOCK__
new NodeSDK({ instrumentations }).start()
`

// Substitute the placeholders at apply time so the generated file carries the
// ServiceNode id (`__SERVICE_NAME__`) and the registered project name
// (`__PROJECT__`) verbatim. The two roles diverged in v0.4.4: the service name
// names the ServiceNode inside one project's graph, the project basename is
// the URL routing key. v0.4.5 adds __INSTRUMENTATION_BLOCK__ — a (possibly
// empty) sequence of `instrumentations.push(...)` calls for non-bundled
// libraries like Prisma whose query engines bypass the auto-instrumentation
// set's coverage.
export function renderNextInstrumentationNode(
  template: string,
  serviceName: string,
  projectName: string,
  registrations: readonly string[] = [],
): string {
  const block = registrations.length === 0 ? '' : `\n${registrations.join('\n')}\n`
  return template
    .replace(/__SERVICE_NAME__/g, serviceName)
    .replace(/__PROJECT__/g, projectName)
    .replace(/__INSTRUMENTATION_BLOCK__\n?/g, block)
}

// ── Next.js edge-runtime template (ADR-126). ─────────────────────────────
//
// `@opentelemetry/sdk-node` cannot run inside Next's Edge runtime — a V8
// isolate with no Node.js API surface (no `node:fs`, no `node:net`). Next's
// own build-time analysis rejects an edge bundle that imports `node:fs`, so
// this file cannot reuse the `OTEL_ENDPOINT_RESOLVER_ESM` daemon.json walk
// the Node file relies on — that walk is exactly the kind of Node API access
// the Edge runtime forbids. `@vercel/otel`'s `registerOTel()` is runtime-aware
// and built on web-standard APIs instead, so one call covers the Edge runtime
// (and, on Vercel, gets Vercel's own OTel integration for free when present).
//
// The endpoint still resolves from the same `OTEL_EXPORTER_OTLP_TRACES_ENDPOINT`
// env var every other generated init reads, defaulting to the same canonical
// `http://localhost:4318/v1/traces` — no new env var, no new config surface
// (framework-installers.md §7, ADR-126). `registerOTel` reads OTLP
// endpoint/protocol/headers from `process.env` itself when `traceExporter`
// is left at its "auto" default, so setting the env vars ahead of the call
// is enough; unlike the Node file there's no filesystem probe to run first.
export const NEXT_INSTRUMENTATION_EDGE_HEADER =
  '// Generated by `neat init --apply` (ADR-126). Next.js edge-runtime instrumentation via @vercel/otel.'

export const NEXT_INSTRUMENTATION_EDGE_TS = `${NEXT_INSTRUMENTATION_EDGE_HEADER}
import { registerOTel } from '@vercel/otel'

process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
process.env.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT ||= 'http://localhost:4318/v1/traces'
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

registerOTel({ serviceName: process.env.OTEL_SERVICE_NAME })
`

// Edge bundles are ESM-only (Next.js requires `import`/`export` syntax for
// any file it loads into the Edge runtime), so the JS flavor is identical to
// the TS flavor byte-for-byte — there's no CJS variant to diverge from, same
// as the top-level instrumentation.{ts,js} gate above.
export const NEXT_INSTRUMENTATION_EDGE_JS = NEXT_INSTRUMENTATION_EDGE_TS

// ── Meta-framework templates (ADR-074 §3). ──────────────────────────────
//
// Remix, SvelteKit, Nuxt, and Astro each own their boot path. The installer
// emits one OTel-init module that loads `.env.neat` and starts the Node SDK,
// then either creates or injects an import into the framework's canonical
// hook file. Templates mirror the Next.js pair in shape — one generated init
// module, one optional framework-hook stub for the absent-file case.

export const FRAMEWORK_OTEL_INIT_HEADER =
  '// Generated by `neat init --apply` (ADR-074). OpenTelemetry SDK hook.'

// Same inline-defaults pattern as the plain-Node + Next templates: env vars
// arrive on `process.env` via the apply-time substitution below so bundler
// path mangling (issue #369) can't lose them. `__SERVICE_NAME__` /
// `__PROJECT__` are substituted via renderFrameworkOtelInit().
const FRAMEWORK_OTEL_INIT_TS_BODY = `${FRAMEWORK_OTEL_INIT_HEADER}
${OTEL_ESM_NODE_IMPORTS}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_ESM}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

import { NodeSDK } from '@opentelemetry/sdk-node'
import { getNodeAutoInstrumentations } from '@opentelemetry/auto-instrumentations-node'

const sdk = new NodeSDK({
  serviceName: process.env.OTEL_SERVICE_NAME,
  instrumentations: [getNodeAutoInstrumentations()],
})
sdk.start()
`

const FRAMEWORK_OTEL_INIT_JS_BODY = `${FRAMEWORK_OTEL_INIT_HEADER}
process.env.OTEL_SERVICE_NAME ||= '__SERVICE_NAME__'
${OTEL_ENDPOINT_RESOLVER_CJS}
${OTEL_OTLP_PROTOCOL_JS}
${OTEL_OTLP_HEADERS_JS}

const { NodeSDK } = require('@opentelemetry/sdk-node')
const { getNodeAutoInstrumentations } = require('@opentelemetry/auto-instrumentations-node')

const sdk = new NodeSDK({
  serviceName: process.env.OTEL_SERVICE_NAME,
  instrumentations: [getNodeAutoInstrumentations()],
})
sdk.start()
`

export function renderFrameworkOtelInit(
  template: string,
  serviceName: string,
  projectName: string,
): string {
  return template
    .replace(/__SERVICE_NAME__/g, serviceName)
    .replace(/__PROJECT__/g, projectName)
}

// Remix — `app/otel.server.{ts,js}` carries the SDK bootstrap. The framework
// loads `entry.server.*` at server-process start, and a top-of-module import
// of `./otel.server` runs the bootstrap before any request handler imports.
export const REMIX_OTEL_SERVER_TS = FRAMEWORK_OTEL_INIT_TS_BODY
export const REMIX_OTEL_SERVER_JS = FRAMEWORK_OTEL_INIT_JS_BODY

// SvelteKit — `src/otel-init.{ts,js}` carries the SDK bootstrap; the
// framework's `src/hooks.server.{ts,js}` imports it. When hooks.server is
// absent, the installer writes the stub below alongside the import.
export const SVELTEKIT_OTEL_INIT_TS = FRAMEWORK_OTEL_INIT_TS_BODY
export const SVELTEKIT_OTEL_INIT_JS = FRAMEWORK_OTEL_INIT_JS_BODY

export const SVELTEKIT_HOOKS_SERVER_TS = `${FRAMEWORK_OTEL_INIT_HEADER}
import './otel-init'

import type { Handle } from '@sveltejs/kit'

export const handle: Handle = async ({ event, resolve }) => {
  return resolve(event)
}
`

export const SVELTEKIT_HOOKS_SERVER_JS = `${FRAMEWORK_OTEL_INIT_HEADER}
import './otel-init'

/** @type {import('@sveltejs/kit').Handle} */
export const handle = async ({ event, resolve }) => {
  return resolve(event)
}
`

// Nuxt — `server/plugins/otel.{ts,js}` is the convention-loaded plugin file;
// it imports the sibling `otel-init.{ts,js}` which carries the SDK bootstrap.
export const NUXT_OTEL_INIT_TS = FRAMEWORK_OTEL_INIT_TS_BODY
export const NUXT_OTEL_INIT_JS = FRAMEWORK_OTEL_INIT_JS_BODY

export const NUXT_OTEL_PLUGIN_TS = `${FRAMEWORK_OTEL_INIT_HEADER}
import './otel-init'

export default defineNitroPlugin(() => {
  // OTel SDK is initialised at module-load via the side-effect import above.
})
`

export const NUXT_OTEL_PLUGIN_JS = `${FRAMEWORK_OTEL_INIT_HEADER}
require('./otel-init')

module.exports = defineNitroPlugin(() => {
  // OTel SDK is initialised at module-load via the side-effect import above.
})
`

// Astro — `src/middleware.{ts,js}` runs once at module-load (and then per
// request via onRequest). The top-of-module import of `./otel-init` boots
// the SDK before the first request lands.
export const ASTRO_OTEL_INIT_TS = FRAMEWORK_OTEL_INIT_TS_BODY
export const ASTRO_OTEL_INIT_JS = FRAMEWORK_OTEL_INIT_JS_BODY

export const ASTRO_MIDDLEWARE_TS = `${FRAMEWORK_OTEL_INIT_HEADER}
import './otel-init'

import { defineMiddleware } from 'astro:middleware'

export const onRequest = defineMiddleware(async (context, next) => {
  return next()
})
`

export const ASTRO_MIDDLEWARE_JS = `${FRAMEWORK_OTEL_INIT_HEADER}
import './otel-init'

import { defineMiddleware } from 'astro:middleware'

export const onRequest = defineMiddleware(async (context, next) => {
  return next()
})
`
